import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFamilyMembers } from '../context/FamilyMembersContext';
import { loginSuccess } from '../slices/authSlice';
import { fetchMemberDashboardSession, markMemberOnboardingComplete } from '../utils/memberPostLogin';
import {
  getStoredMemberToken,
  handleMemberSessionFailure,
} from '../utils/memberSession';
import './Login.css';
import './Proceed.css';
import './OnboardingFamily.css';

const EMPTY_FORM = {
  name: '',
  age: 25,
  gender: 'Male',
  phone: '',
  relationship: 'Spouse',
};

export default function OnboardingFamily() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const authKind = useSelector((state) => state.auth.authKind);
  const roles = useSelector((state) => state.auth.roles);
  const { familyMembers, addFamilyMember, maxFamilyMembers, loading } = useFamilyMembers();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skipBusy, setSkipBusy] = useState(false);
  const [skipError, setSkipError] = useState('');
  const [gateLoading, setGateLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    if (authKind === 'ops') {
      navigate('/ops', { replace: true });
      return;
    }
    if (authKind === 'doctor') {
      navigate('/doctor/pending', { replace: true });
      return;
    }

    let cancelled = false;
    setGateLoading(true);
    const authToken = getStoredMemberToken(token);
    fetchMemberDashboardSession(authToken)
      .then(({ ok, status, data }) => {
        if (cancelled) return;
        if (!ok) {
          const outcome = handleMemberSessionFailure({
            status,
            data,
            dispatch,
            navigate,
          });
          if (outcome === 'retry') {
            setSkipError(
              status === 503
                ? data.error || 'Unable to verify account access right now. Please try again.'
                : 'Could not verify your account. Please try again.'
            );
          }
          return;
        }
        if (data.needsProfileCompletion) {
          navigate('/login/complete-profile', { replace: true });
          return;
        }
        if (!data.needsMemberOnboarding) {
          navigate('/dashboard', { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setGateLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, authKind, navigate, dispatch]);

  if (!token || authKind === 'ops' || authKind === 'doctor') {
    return null;
  }

  if (gateLoading) {
    return (
      <div className="proceed-page">
        <LoadingSpinner className="ops-loading" label="Loading" />
      </div>
    );
  }

  const isAtMemberLimit = familyMembers.length >= maxFamilyMembers;

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'age' ? parseInt(value, 10) || 25 : value,
    }));
  }

  async function handleAddMember(e) {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Please enter a name.');
      return;
    }
    if (isAtMemberLimit) {
      setFormError(`You can add only ${maxFamilyMembers} family members.`);
      return;
    }

    setSubmitting(true);
    const success = await addFamilyMember(formData);
    setSubmitting(false);

    if (success) {
      setFormData(EMPTY_FORM);
      return;
    }
    setFormError('Could not save member. Please try again.');
  }

  async function goToDashboard() {
    setSkipError('');
    setSkipBusy(true);
    const result = await markMemberOnboardingComplete(token);
    setSkipBusy(false);

    if (!result.ok) {
      setSkipError(result.error || 'Could not continue. Please try again.');
      return;
    }

    if (result.user) {
      dispatch(
        loginSuccess({
          token,
          user: result.user,
          roles,
          authKind: 'member',
        })
      );
    }

    navigate('/dashboard', { replace: true });
  }

  return (
    <div className="proceed-page">
      <div className="proceed-bg-shapes" aria-hidden="true">
        <span className="proceed-shape proceed-shape-1" />
        <span className="proceed-shape proceed-shape-2" />
      </div>

      <Link to="/" className="proceed-logo">
        <img src="/images/unaffectedLogo.png" alt="" className="proceed-logo-image" />
        <span className="proceed-logo-text">Unaffected Healthcare</span>
      </Link>

      <div className="login-page onboarding-family-page">
        <div className="login-card-wrap">
          <div className="login-card onboarding-family-card">
            <h1 className="login-title login-title-form">Add family members (optional)</h1>
            <p className="login-subtitle">
              Step 2 of 2 — add spouse, parents, or children so you can book lab tests, consults, and
              medicines for them easily.
            </p>

            {familyMembers.length > 0 && (
              <div className="onboarding-family-list">
                <h2 className="onboarding-family-list-title">Added members</h2>
                <ul>
                  {familyMembers.map((member) => (
                    <li key={member.id}>
                      <strong>{member.name}</strong>
                      <span>{member.relationship}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isAtMemberLimit && (
              <form className="login-form onboarding-family-form" onSubmit={handleAddMember}>
                <div className="form-group">
                  <label htmlFor="onboarding-name">Name</label>
                  <input
                    id="onboarding-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    className="login-input"
                    required
                  />
                </div>

                <div className="onboarding-family-row">
                  <div className="form-group">
                    <label htmlFor="onboarding-age">Age</label>
                    <input
                      id="onboarding-age"
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      min="1"
                      max="120"
                      className="login-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="onboarding-gender">Gender</label>
                    <select
                      id="onboarding-gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="login-input"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="onboarding-relationship">Relationship</label>
                  <select
                    id="onboarding-relationship"
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleInputChange}
                    className="login-input"
                  >
                    <option value="Spouse">Spouse</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="onboarding-phone">Phone (optional)</label>
                  <input
                    id="onboarding-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile"
                    className="login-input"
                  />
                </div>

                {formError && (
                  <p className="login-inline-error" role="alert">
                    {formError}
                  </p>
                )}

                <button type="submit" className="login-btn-submit" disabled={submitting || loading}>
                  {submitting ? 'Adding…' : 'Add member'}
                </button>
              </form>
            )}

            {isAtMemberLimit && (
              <p className="onboarding-family-limit-note">
                You have reached the maximum of {maxFamilyMembers} family members.
              </p>
            )}

            <div className="onboarding-family-actions">
              {skipError && (
                <p className="login-inline-error" role="alert">
                  {skipError}
                </p>
              )}
              <button
                type="button"
                className="login-btn-submit"
                onClick={goToDashboard}
                disabled={skipBusy || submitting}
              >
                {skipBusy
                  ? 'Saving…'
                  : familyMembers.length > 0
                  ? 'Continue to dashboard'
                  : 'Skip for now'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
